import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { fetchAuth } from '../common/api';
import { v4 as uuidv4 } from 'uuid';

const SimulationContext = createContext();

function SimulationProvider({ children }) {
  const [simulations, setSimulations] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingQueue, setPendingQueue] = useState([]);
  const [pendingSimulation, setPendingSimulation] = useState(null);
  const [finalQueue, setFinalQueue] = useState([]);
  const [grassType, setGrassType] = useState("");

  const isProcessing = useRef(false);

  // 🟩 Lancement des simulations après confirmation
  const processQueue = async () => {
    if (finalQueue.length === 0 || isProcessing.current) return;
    isProcessing.current = true;  
    const BATCH_SIZE = 40;
    const queueCopy = [...finalQueue];
    
    while (queueCopy.length > 0) {
      const currentBatch = queueCopy.splice(0, BATCH_SIZE);
      const formData = new FormData();
      const simulation_ids = [];
  
      currentBatch.forEach(({ simulationId, playerId, file, fieldType, isApi }) => {
        formData.append("simulation_ids[]", simulationId);
        formData.append("player_ids[]", playerId);
        formData.append("field_type", fieldType);
        formData.append("is_api_mode", isApi);
        simulation_ids.push(simulationId);
        if (isApi) {
          formData.append("activity_ids[]", file);
        } else {
          formData.append("files", file);
        }
      });
  
      try {
        const response = await fetchAuth(`/simulations`, {
          method: "POST",
          body: formData,
        });
  
        if (!response.ok) throw new Error("Erreur lors du lancement des simulations");
        await response.json();
  
        setFinalQueue((prev) =>
          prev.filter(item => !simulation_ids.includes(item.simulationId))
        );
  
        setSimulations((prev) => {
          const updated = { ...prev };
          simulation_ids.forEach((id) => {
            updated[id] = { inProgress: true };
          });
          return updated;
        });
        fetchSimulationStatus(); 
      } catch (error) {
        
        break; // 🔴 Stoppe si une requête échoue
      }
    }
  
    isProcessing.current = false;
  };
  
  
  // 🕐 Polling pour récupérer les statuts de simulation depuis la BDD
const fetchSimulationStatus = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    
    return;
  }

  try {
    const response = await fetchAuth(`/simulations/status`);
    if (!response.ok) throw new Error("Erreur de récupération des statuts");
    const data = await response.json();
    setSimulations(data);
  } catch (error) {
    
  }
};

useEffect(() => {
  if (!localStorage.getItem("token")) return; // 🛑 bloque si pas logué
  fetchSimulationStatus();
}, []);

// 🔁 Poll seulement s’il y a au moins une simulation active
useEffect(() => {
  const hasSimulations = Object.values(simulations).some(
    s => s.status === "in_progress" || s.status === "queued"
  );

  if (!hasSimulations) return;

  const interval = setInterval(fetchSimulationStatus, 2000);
  return () => clearInterval(interval);
}, [simulations]);


  // ✅ Vérifie s’il existe déjà une simulation
  const checkExistingSimulation = async (playerId, fileOrActivityId, fieldType, isApi = false) => {
    if (isApi) {
      // 🟢 On ajoute directement à la queue sans appeler l'API /exists
      addToFinalQueue(playerId, fileOrActivityId, fieldType, true);
      return;
    }
  
    try {
      const formData = new FormData();
      formData.append("player_id", playerId);
      formData.append("file", fileOrActivityId);
      formData.append("fieldType", fieldType);
      const response = await fetchAuth(`/simulations/exists`, {
        method: "POST",
        body: formData,
      });
  
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Vérification échouée");
  
      if (data.exists) {
        setPendingQueue((prev) => [...prev, {
          playerId,
          file: fileOrActivityId,
          fieldType,
          existingSimulationId: data.simulation_id,
          playerName: data.player_name,
          simulationDate: data.simulation_date,
        }]);
      } else {
        addToFinalQueue(playerId, fileOrActivityId, fieldType);
      }
  
    } catch (error) {
     
    }
  };
  
  

  const addToFinalQueue = (playerId, fileOrActivityId, fieldType, isApi = false) => {
    const simulationId = uuidv4();
    setFinalQueue((prev) => [...prev, {
      simulationId,
      playerId,
      file: fileOrActivityId,
      fieldType,
      isApi,
    }]);
  };
  

  const addSimulation = (playerId, fileOrActivityId, fieldType, isApi = false) => {
    setGrassType(fieldType);
    checkExistingSimulation(playerId, fileOrActivityId, fieldType, isApi);
  };
  

  useEffect(() => {
    if (pendingQueue.length > 0 && !pendingSimulation) {
      setPendingSimulation(pendingQueue[0]);
      setShowConfirmation(true);
    }
  }, [pendingQueue]);

  useEffect(() => {
    if (pendingQueue.length === 0 && !showConfirmation && finalQueue.length > 0) {
      processQueue();
    }
  }, [pendingQueue, showConfirmation, finalQueue]);

  const confirmReplaceSimulation = async () => {
    if (!pendingSimulation) return;

    try {
      await fetchAuth(`/simulations/${pendingSimulation.existingSimulationId}`, {
        method: "DELETE",
      });

      addToFinalQueue(pendingSimulation.playerId, pendingSimulation.file, pendingSimulation.fieldType);
    } catch (error) {
      
    }

    advanceQueue();
  };

  const cancelSimulation = () => {
    advanceQueue();
  };

  const advanceQueue = () => {
    setPendingQueue((prev) => {
      const updated = prev.slice(1);
      setPendingSimulation(updated[0] || null);
      setShowConfirmation(updated.length > 0);
      return updated;
    });
  };

  return (
    <SimulationContext.Provider value={{ simulations, addSimulation }}>
      {children}

      {showConfirmation && pendingSimulation && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            <h2 className="text-lg font-bold mb-4">
              Existing simulation for this date ({pendingSimulation.simulationDate}) for {pendingSimulation.playerName}
            </h2>
            <p>Do you want to overwrite it?</p>
            <div className="mt-4 flex justify-center space-x-4">
              <button className="px-4 py-2 bg-red-500 text-white rounded-md" onClick={confirmReplaceSimulation}>
                Yes, replace
              </button>
              <button className="px-4 py-2 bg-gray-500 text-white rounded-md" onClick={cancelSimulation}>
                No, cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </SimulationContext.Provider>
  );
}

const useSimulation = () => useContext(SimulationContext);

export { SimulationProvider, useSimulation };
